/**
 * Icon set, SVG primitives that replace every emoji in the app.
 *
 * Per Ean's 2026-06-03 rule + the rebuild plan: zero emojis in the codebase.
 * Each icon takes `size` (default 16) and inherits `currentColor` so it
 * paints to the surrounding text color. Single-color marks. Stroke-based
 * line work matches the Olytic mark aesthetic.
 *
 * Add new icons as needed. Keep marks minimal; we are not building a
 * fully-figured icon set, just enough to cover the surfaces we have.
 */

import type { CSSProperties } from 'react';

interface IconProps {
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** Aria-label, when the icon stands on its own without adjacent text. */
  label?: string;
}

function svg(path: React.ReactNode, { size = 16, className, style, label }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
      role={label !== undefined ? 'img' : 'presentation'}
      aria-label={label}
      aria-hidden={label === undefined}
    >
      {path}
    </svg>
  );
}

// Push 5-post-smoke-25a (2026-07-14): redrawn to match Lucide `users`.
// Prior secondary person was an open half-circle arc which read as "half
// a face" against the primary's full head circle. New shape draws two
// real head circles + two shoulder arcs so both persons are visually
// symmetric — no ambiguous half-shapes.
export function IconPeople(props: IconProps = {}) {
  return svg(
    <>
      {/* Primary person — larger, foreground */}
      <circle cx="6" cy="6.5" r="2.3" />
      <path d="M2 13c0-2.2 1.8-4 4-4s4 1.8 4 4" />
      {/* Secondary person — smaller, background right */}
      <circle cx="11.3" cy="5.5" r="1.7" />
      <path d="M14 12.5c0-1.5-.95-2.85-2.4-3.35" />
    </>,
    props,
  );
}

export function IconPlug(props: IconProps = {}) {
  return svg(
    <>
      <path d="M6 2v3M10 2v3" />
      <rect x="4.5" y="5" width="7" height="4" rx="1" />
      <path d="M8 9v3a2 2 0 0 1-2 2H4" />
    </>,
    props,
  );
}

export function IconLock(props: IconProps = {}) {
  return svg(
    <>
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </>,
    props,
  );
}

// Push 5-post-smoke-25a (2026-07-14): redrawn to a proper 4-point AI
// sparkle (Anthropic / Claude vocabulary). Prior shape was 8 rays out
// of an empty center (compass-rose read); new shape is a single
// pointed-diamond star that reads as "AI / generated / magic" the
// moment it appears — matching Lucide `sparkles` and the sparkle
// glyph on Claude.ai, ChatGPT, Notion AI, etc.
export function IconSparkle(props: IconProps = {}) {
  return svg(
    <path d="M8 1.75L9.35 6.65L14.25 8L9.35 9.35L8 14.25L6.65 9.35L1.75 8L6.65 6.65Z" />,
    props,
  );
}

export function IconMail(props: IconProps = {}) {
  return svg(
    <>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <path d="M2.5 4.5l5.5 4 5.5-4" />
    </>,
    props,
  );
}

export function IconSearch(props: IconProps = {}) {
  return svg(
    <>
      <circle cx="7" cy="7" r="4" />
      <path d="M10 10l3 3" />
    </>,
    props,
  );
}

export function IconPlus(props: IconProps = {}) {
  return svg(<path d="M8 3v10M3 8h10" />, props);
}

/**
 * IconMicrophone — voice-input affordance for the chat composer footer
 * (Push #59 / Wave F1, 2026-06-15). Capsule body + stand + base. Single
 * mark, two strokes — matches the existing Icon set's stroke aesthetic.
 */
export function IconMicrophone(props: IconProps = {}) {
  return svg(
    <>
      <rect x="6" y="2" width="4" height="7" rx="2" />
      <path d="M4 7a4 4 0 0 0 8 0" />
      <path d="M8 11v3" />
      <path d="M5.5 14h5" />
    </>,
    props,
  );
}

/**
 * IconPin — pushpin glyph for "pinned" affordances (chat history rail
 * pinned section, future pinned-record affordances).
 *
 * Push 64 Sub-agent YY (2026-07-16) — DIAGONAL redraw per Ean directive:
 * "The pin icon in chat should be diagonal and look more like Claude's
 * image." Prior vertical pushpin (Push 5-post-smoke-47) read as a
 * lantern/scepter at rail sizes and didn't match the Claude / Notion /
 * Slack pushpin vocabulary, which is angled at ~45° so the pin appears
 * to have just been pressed into a surface.
 *
 * Push 68 Sub-agent CCC (2026-07-17) — rotation flipped from -45° to
 * +45° per Ean directive: "Can the diagonal Pin go the other way to
 * match the pencil icon." IconPencil (see below in this file) draws
 * a pencil with the eraser cap at upper-RIGHT (x≈11.5-13.5, y≈2.5-4.5)
 * and the sharpened tip at lower-LEFT (x≈2.5, y≈13.5) — i.e. the
 * pencil runs upper-right → lower-left ("/" slant). Prior pin with
 * rotate(-45) ran the OPPOSITE way (head upper-LEFT → tip lower-RIGHT,
 * "\" slant). Swapping to rotate(45) puts the pin on the same "/"
 * slant as pencil so the row-hover overflow menu (Rename / Pin ·
 * Unpin) reads as one visually-consistent icon language.
 *
 * Geometry: the canonical thumbtack silhouette rotated +45° around the
 * viewBox center (8, 8). Result:
 *   - HEAD (cap you press) sits at upper-RIGHT (~x=12, y=4)
 *   - FLANGE band runs perpendicular to the diagonal shaft
 *   - SHAFT angles down-left from head to point
 *   - POINT tip sits at lower-LEFT (~x=4, y=12)
 *
 * Reads unambiguously as "pushpin, angled" at every render size 11-24px.
 * strokeLinejoin="round" softens the rotated corners so no jaggedness
 * appears from the transform.
 */
export function IconPin(props: IconProps = {}) {
  return svg(
    <g transform="rotate(45 8 8)">
      <path d="M6 2 H10 V5 H12 V8 H9 V12 L8 14 L7 12 V8 H4 V5 H6 Z" />
    </g>,
    props,
  );
}

export function IconChevron(
  props: IconProps & { direction?: 'down' | 'up' | 'left' | 'right' } = {},
) {
  const { direction = 'down', ...rest } = props;
  const rotate =
    direction === 'up' ? -90 : direction === 'left' ? 180 : direction === 'right' ? 0 : 90;
  return svg(<path d="M3 6l5 5 5-5" />, {
    ...rest,
    style: { ...(rest.style ?? {}), transform: `rotate(${rotate - 90}deg)` },
  });
}

export function IconWave(props: IconProps = {}) {
  // Two wave lines, each 12 units wide (4 cubic segments × 3 units), centered
  // on the 16-unit viewBox: starts at x=2, ends at x=14, so 2-unit margins on
  // both sides. Prior version started at x=3 which left the path visually
  // right-shifted; user caught it on the Prompts ObjectIcon at lg size.
  return svg(
    <>
      <path d="M2 11c1 1 2 1 3 0 1-1 2-1 3 0 1 1 2 1 3 0 1-1 2-1 3 0" />
      <path d="M2 7c1 1 2 1 3 0 1-1 2-1 3 0 1 1 2 1 3 0 1-1 2-1 3 0" />
    </>,
    props,
  );
}

export function IconBolt(props: IconProps = {}) {
  return svg(<path d="M9 2L4 9h3.5L7 14l5-7H8.5L9 2z" />, props);
}

export function IconClose(props: IconProps = {}) {
  return svg(<path d="M3 3l10 10M13 3L3 13" />, props);
}

export function IconCheck(props: IconProps = {}) {
  return svg(<path d="M3 8l3.5 3.5L13 5" />, props);
}

/**
 * Push #78 round 23 (Ean dogfood 2026-06-16 #3) — paperclip glyph for the
 * file-attach affordance in the chat composer footer. Sits next to the
 * Web search globe + Chat settings wrench in the footer's left zone. The
 * paperclip is universal-language for "attach a file" — same vocabulary
 * Gmail, Slack, Claude.ai, ChatGPT, and every other chat surface uses,
 * so users don't have to learn it. Drawn as a single curved path
 * approximating a paperclip's outline on the 16-grid.
 */
export function IconPaperclip(props: IconProps = {}) {
  // Push #84 (Ean dogfood 2026-06-19) — replaced the homemade single-
  // curve path with a Lucide-style paperclip outline scaled to 16-grid.
  // The previous version drew one curved stroke wrapped in a rotation
  // transform; at small sizes it read as a generic angle (or a lowercase
  // J), not a paperclip. The new geometry shows the classic
  // two-parallel-lines-with-U-turn shape that's universally recognized
  // as "attach". Stroke-only (no fill) with round caps + joins for the
  // friendly cap at the open tail.
  return svg(
    <path
      d="M 14.3 7.37 l -6.13 6.13 a 4 4 0 0 1 -5.66 -5.66 l 6.13 -6.13 a 2.67 2.67 0 0 1 3.77 3.77 l -6.13 6.13 a 1.33 1.33 0 0 1 -1.89 -1.89 l 5.66 -5.66"
      strokeLinecap="round"
      strokeLinejoin="round"
    />,
    props,
  );
}

/**
 * Push #78 round 21 (item G) — warning triangle used for the Incidents
 * nav surface (admin sidebar + ObjectIcon 'incidents' kind). Triangle
 * outline with an interior exclamation: bar + dot. Same 16-grid as the
 * other Icon* primitives. Used at xs (sidebar tile) + lg (page-header
 * lightning-highlight) — sizing controlled by props.size as elsewhere.
 */
// Push 5-post-smoke-25a (2026-07-14): exclamation dot upgraded from a
// zero-length stroke (0.1 unit tall — read as a hairline speck) to a
// proper filled circle. Fills at 0.6px radius so it reads as a real
// dot at 14-24px sizes instead of a broken pixel. Triangle geometry
// preserved.
export function IconAlert(props: IconProps = {}) {
  return svg(
    <>
      {/* Outer triangle */}
      <path d="M8 2L14 13H2Z" />
      {/* Exclamation bar */}
      <path d="M8 6V9.5" />
      {/* Exclamation dot — proper filled circle */}
      <circle cx="8" cy="11.4" r="0.6" fill="currentColor" stroke="none" />
    </>,
    props,
  );
}

/**
 * Push #76O — trash can mark used for every destructive action.
 * Replaces the "×" character that was previously used for delete /
 * remove. Per Ean ruling 2026-06-15: destructive controls should be a
 * recognizable trash can icon, always tinted red so the destructive
 * identity is signaled at rest (not just on hover).
 */
// Push 5-post-smoke-25a (2026-07-14): redrawn to match Lucide `trash-2`.
// Prior shape drew the body with tapered/slanted sides which gave it a
// squat, cartoonish look (old Microsoft trash-can vocabulary); new shape
// uses vertical sides with a small round-corner base + a proper flat
// handle bump on the lid. Two inner content lines preserved (Lucide-style
// receptacle indicators). Reads as "delete" at every size 12-24px.
export function IconTrash(props: IconProps = {}) {
  return svg(
    <>
      {/* Lid */}
      <path d="M2.5 4H13.5" />
      {/* Handle bump on top of lid */}
      <path d="M6 4V2.75A.75.75 0 0 1 6.75 2H9.25A.75.75 0 0 1 10 2.75V4" />
      {/* Body — vertical sides + slight round at base corners */}
      <path d="M4 4V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4" />
      {/* Two inner content indicators */}
      <path d="M6.5 7V11.25" />
      <path d="M9.5 7V11.25" />
    </>,
    props,
  );
}

// ---------------------------------------------------------------------
// Push #12 object icons — vocabulary expansion for the ObjectIcon tile
// system (apps/ui/src/components/ObjectIcon.tsx). One icon per noun the
// app surfaces; never reuse a mark for two different objects so the
// visual anchor stays unambiguous as users move between pages.
// ---------------------------------------------------------------------

export function IconInbox(props: IconProps = {}) {
  return svg(
    <>
      <path d="M2 9l1.5-5h9L14 9" />
      <path d="M2 9v4a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9" />
      <path d="M2 9h3.5l1 1.5h3l1-1.5H14" />
    </>,
    props,
  );
}

export function IconReceipt(props: IconProps = {}) {
  return svg(
    <>
      <path d="M3.5 2v12l1.5-1 1.5 1 1.5-1 1.5 1 1.5-1 1.5 1V2" />
      <path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" />
    </>,
    props,
  );
}

// Push 5-post-smoke-25a (2026-07-14): redrawn to a Lucide `settings`
// cog silhouette. Prior shape was a center circle with 8 straight
// spokes extending outward — read as "compass rose / burst" rather
// than "gear teeth" per Ean's clunky-icon gut check. New geometry
// draws a rounded gear body with 8 nubs around the perimeter (path
// bulges out at each tooth position) + a small inner hub circle,
// matching the Anthropic/Claude/iOS "settings" vocabulary.
//
// Push 5-post-smoke-32 (Sub-agent U, 2026-07-15) — kept IconGear on
// this Lucide-settings silhouette. The Workspace admin sidebar's
// tile icon swapped to IconBriefcase per Ean's "gear still ugly + a
// briefcase would fit the concept" gut check; IconGear itself stays
// reserved for chat-side "settings/preferences" surfaces (Chat
// CapabilitiesPanel glyph + any future chat-settings affordance)
// where the gear vocabulary still reads correctly.
export function IconGear(props: IconProps = {}) {
  return svg(
    <>
      {/* Gear body — 8-tooth silhouette tightened to fit within the
         16-viewBox at strokeWidth 1.6 (leftmost point ~x=1.2 leaves
         0.4px inset from canvas edge after the half-stroke). Path
         approximates Lucide `settings`: rounded gear body with 8
         nubs around the perimeter. */}
      <path d="M8.7 2h-1.4a.8 .8 0 0 0-.79.66l-.11.66a5.5 5.5 0 0 0-.99.57l-.61-.28a.8 .8 0 0 0-1 .29l-.7.7a.8 .8 0 0 0-.29 1l.28.61a5.5 5.5 0 0 0-.57.99l-.66.11a.8 .8 0 0 0-.66.79v1.4a.8 .8 0 0 0 .66.79l.66.11a5.5 5.5 0 0 0 .57.99l-.28.61a.8 .8 0 0 0 .29 1l.7.7a.8 .8 0 0 0 1 .29l.61-.28a5.5 5.5 0 0 0 .99.57l.11.66a.8 .8 0 0 0 .79.66h1.4a.8 .8 0 0 0 .79-.66l.11-.66a5.5 5.5 0 0 0 .99-.57l.61.28a.8 .8 0 0 0 1-.29l.7-.7a.8 .8 0 0 0 .29-1l-.28-.61a5.5 5.5 0 0 0 .57-.99l.66-.11a.8 .8 0 0 0 .66-.79v-1.4a.8 .8 0 0 0-.66-.79l-.66-.11a5.5 5.5 0 0 0-.57-.99l.28-.61a.8 .8 0 0 0-.29-1l-.7-.7a.8 .8 0 0 0-1-.29l-.61.28a5.5 5.5 0 0 0-.99-.57l-.11-.66a.8 .8 0 0 0-.79-.66z" />
      {/* Inner hub */}
      <circle cx="8" cy="8" r="2" />
    </>,
    props,
  );
}

// Push 5-post-smoke-32 (Sub-agent U, 2026-07-15) — new mark for the
// Workspace admin surface. Prior tile icon was IconGear which Ean
// twice called ugly; the Workspace concept is broader than "settings"
// anyway (it holds general prefs + usage + billing + notifications
// tabs), so a briefcase reads truer: "your team's workspace, the
// container." Shape: classic briefcase silhouette — rounded body +
// handle bar on top + faint horizontal divider across the middle.
// Draws in 16-viewBox at strokeWidth 1.6, matching the rest of the
// obsidian-tile glyphs (People, Permission sets, Activity).
export function IconBriefcase(props: IconProps = {}) {
  return svg(
    <>
      {/* Handle — small U-shape sitting above the body. */}
      <path d="M6 4V3.2A1.2 1.2 0 0 1 7.2 2h1.6A1.2 1.2 0 0 1 10 3.2V4" />
      {/* Body — rounded rectangle. Slight bottom overhang for
         briefcase feel (bag corners tucked, top-open). */}
      <rect x="2" y="4" width="12" height="9" rx="1.5" />
      {/* Front-clasp divider — a faint horizontal line splitting the
         body two-thirds down, with a tiny center latch bump. */}
      <path d="M2 8.5H14" />
    </>,
    props,
  );
}

// Push #72 — chat settings affordance. Renders in the composer footer
// left cluster + the CapabilitiesPanel rail card header.
// Push 5-post-smoke-46 (Sub-agent HH, 2026-07-15) — redrawn from Lucide
// `settings-2` (two horizontal sliders with circle knobs) to actual
// Lucide `wrench`. Ean twice flagged the slider silhouette as "still
// fucking line art not wrench"; the sliders shape reads as "adjust
// values" but not as "wrench" at a glance. New shape is Lucide's
// canonical crescent wrench outline scaled from 24-viewBox to the
// 16-viewBox convention (each coord multiplied by 2/3):
//   - Head at the upper-right with an open bolt-cutout jaw
//   - Diagonal handle from the head down to the lower-left tip
//   - Small rounded tip corner at the bottom-left
// Real mechanic's wrench, not sliders. Reads as "wrench" at every
// composer chip + rail sliver + expanded panel header size (12-18px).
// Name preserved (IconWrench) so every callsite continues to work.
export function IconWrench(props: IconProps = {}) {
  return svg(
    <path d="M9.8 4.2 a0.667 0.667 0 0 0 0 0.933 l1.067 1.067 a0.667 0.667 0 0 0 0.933 0 l2.513 -2.513 a4 4 0 0 1 -5.293 5.293 l-4.607 4.607 a1.413 1.413 0 0 1 -2 -2 l4.607 -4.607 a4 4 0 0 1 5.293 -5.293 l-2.507 2.507 z" />,
    props,
  );
}

export function IconUserCircle(props: IconProps = {}) {
  return svg(
    <>
      {/* Push #40. tightened shoulder path so endpoints stay INSIDE the
         outer circle. Previously (3.5, 13) → (12.5, 13) poked through the
         r=6.2 boundary at y=13 (circle width there is ~7.3, x ∈ [4.3, 11.7]).
         New endpoints (4.6, 12.7) → (11.4, 12.7) fit cleanly inside. */}
      <circle cx="8" cy="8" r="6.2" />
      <circle cx="8" cy="6.5" r="2" />
      <path d="M4.6 12.7c.5-2 2-3.2 3.4-3.2s2.9 1.2 3.4 3.2" />
    </>,
    props,
  );
}

export function IconGrid(props: IconProps = {}) {
  return svg(
    <>
      <rect x="2.5" y="2.5" width="4.5" height="4.5" rx="1" />
      <rect x="9" y="2.5" width="4.5" height="4.5" rx="1" />
      <rect x="2.5" y="9" width="4.5" height="4.5" rx="1" />
      <rect x="9" y="9" width="4.5" height="4.5" rx="1" />
    </>,
    props,
  );
}

// Push 5-post-smoke-1 (2026-07-11): IconChat redrawn as an iOS-native
// message bubble — rounded rectangle body with a small pointed tail in
// the bottom-left corner, no text lines inside. The prior variant carried
// a Microsoft-era "speech bubble w/ text lines" shape that read as legacy
// SaaS chat; the new stroke reads iOS Messages / iMessage-shape at every
// size. Sole canonical chat glyph — every callsite (SessionList empty
// state, ObjectIcon.chat, future chat-title marks) now inherits the iOS
// vocabulary. IconChatPlus and IconChatSparkle keep their prior body
// shape (they carry additional glyphs so the tail treatment differs).
export function IconChat(props: IconProps = {}) {
  return svg(
    <>
      {/* Rounded rectangle body + pointed tail bottom-left. Tail is a
         short outward stroke from the lower-left corner so the shape
         reads as "message came out of this bubble" rather than a
         generic rounded rect. */}
      <path d="M3.5 3.5h9a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H6.5l-2.5 2.2V4.5a1 1 0 0 1 1-1z" />
    </>,
    props,
  );
}

export function IconLayers(props: IconProps = {}) {
  // Three stacked layers — used by the Capabilities parent in the admin sidebar.
  // Visual: top layer narrow, middle wider, bottom wider — suggests "stack
  // of things" without being a specific noun (avoids collision with the
  // IconGrid that's already taken by Catalog).
  return svg(
    <>
      <path d="M8 2L2 5l6 3 6-3-6-3z" />
      <path d="M2 8l6 3 6-3" />
      <path d="M2 11l6 3 6-3" />
    </>,
    props,
  );
}

export function IconHome(props: IconProps = {}) {
  return svg(
    <>
      <path d="M2.5 7.5L8 2.5l5.5 5" />
      <path d="M3.5 7v6.5h9V7" />
      <path d="M6.5 13.5V10h3v3.5" />
    </>,
    props,
  );
}

export function IconGroup(props: IconProps = {}) {
  // Push #76F — three horizontal bars of decreasing length, the classic
  // "group / filter / sort" affordance. Used in the chat history rail
  // header to replace the inline "GROUP BY" label + select. Click opens
  // a tiny popover with the grouping options.
  return svg(
    <>
      <path d="M2.5 4h11" />
      <path d="M4 8h8" />
      <path d="M6 12h4" />
    </>,
    props,
  );
}

export function IconShare(props: IconProps = {}) {
  // Push #4.6 (D-187) — generic share affordance used in the chat rail
  // "Share this chat" menu item + the InlineChatTitle overflow. Three
  // connected dots in a triangular arrangement (top + bottom-left +
  // bottom-right) — the canonical "share" mark across Android, Linux,
  // and most web apps. Distinct from IconShareIn/Out which carry
  // direction; this one is the pure action verb.
  return svg(
    <>
      <circle cx="12" cy="4" r="1.6" />
      <circle cx="4" cy="8" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <path d="M5.4 7.2L10.6 4.8" />
      <path d="M5.4 8.8L10.6 11.2" />
    </>,
    props,
  );
}

export function IconShareOut(props: IconProps = {}) {
  // Push #4.6 (D-187) — sharer-side row indicator. Person + outgoing
  // arrow. Reads as "I shared this with someone." Inline 12–14px next to
  // the session title in the chat history rail when is_shared_by_me is
  // true. Same stroke weight as the other 16-viewBox icons so it sits
  // cleanly beside the pin icon + folder chevron.
  return svg(
    <>
      <circle cx="5.5" cy="5.5" r="2" />
      <path d="M2 13c0-2 1.6-3.5 3.5-3.5S9 11 9 13" />
      <path d="M10 4l3 2.5-3 2.5" />
      <path d="M13 6.5H8.5" />
    </>,
    props,
  );
}

export function IconShareIn(props: IconProps = {}) {
  // Push #4.6 (D-187) — recipient-side row indicator. Person + incoming
  // arrow. Reads as "someone shared this with me." Inline 12–14px next
  // to the session title in the chat history rail when is_shared_with_me
  // is true. Mirror of IconShareOut.
  return svg(
    <>
      <circle cx="5.5" cy="5.5" r="2" />
      <path d="M2 13c0-2 1.6-3.5 3.5-3.5S9 11 9 13" />
      <path d="M14 4l-3 2.5 3 2.5" />
      <path d="M11 6.5h4.5" />
    </>,
    props,
  );
}

export function IconCoin(props: IconProps = {}) {
  // Nitpick N14 (2026-06-25) — credits / token / debit unit. Outer round
  // coin body + inner $ glyph (universal "this is a credit unit" icon
  // shape, à la Heroicons currency-dollar, Phosphor coin). Used for the
  // Credits sliver in the chat right-rail collapsed state + (future N15
  // sweep) on every surface that talks about credits, debits, or tokens.
  return svg(
    <>
      <circle cx="8" cy="8" r="6.2" />
      <path d="M8 4.6 V11.4" />
      <path d="M9.9 6.4 Q9.9 5.4 8 5.4 Q6.1 5.4 6.1 6.8 Q6.1 7.8 8 8 Q9.9 8.3 9.9 9.5 Q9.9 10.7 8 10.7 Q6.1 10.7 6.1 9.6" />
    </>,
    props,
  );
}

/**
 * Phase 2 Block 2B (2026-06-30) — shield glyph for operator-only records.
 * Per execution plan § Block 2C icon mapping: all operator-only tables
 * (sys__pricing_config, sys__operator_alert, sys__incident, mcp__oauth_*,
 * sys__*_routing, stripe_webhook_received, schema_migrations) share the
 * shield mark. Reads as "platform infrastructure" — the visual anchor for
 * "you can navigate here but the platform owns the data, not you."
 */
export function IconShield(props: IconProps = {}) {
  return svg(
    <>
      {/* Classic heater-shield outline — pointed bottom, flat top */}
      <path d="M8 1.5 L13.5 3.5 V8 Q13.5 11.5 8 14.5 Q2.5 11.5 2.5 8 V3.5 Z" />
      {/* Inner check mark — connotes "secured / vetted" rather than
         just a hollow shield (which can read as "default" or "neutral"). */}
      <path d="M5.5 7.8 L7.3 9.5 L10.5 6.3" />
    </>,
    props,
  );
}

/**
 * Phase 2 Block 2B (2026-06-30) — link glyph for junction-record audit
 * pages. Per execution plan § Block 2C icon mapping: junctions
 * (sys__permission_set_user, sys__permission_set_*_assignment,
 * mcp__connector_assignment, llm__model_assignment, etc.) share the
 * chain-link mark. Reads as "edge between two records."
 */
export function IconLink(props: IconProps = {}) {
  return svg(
    <>
      {/* Two interlocking chain-link rounded rects, slightly offset.
         The left link tilts down-left, the right link tilts up-right;
         their centers overlap so visually they read as joined. */}
      <path d="M6 8 L4 10 A2.1 2.1 0 1 1 7 13 L9 11" />
      <path d="M10 8 L12 6 A2.1 2.1 0 1 0 9 3 L7 5" />
    </>,
    props,
  );
}

/**
 * Push 52 (2026-07-15, Ean smoke walk) — dedicated glyph for the
 * Assignments admin surface. Ean's frame: "I'm not really sure what
 * the assignments one is, so maybe we need to relook at the icon for
 * the assignments page on admin." The prior chain-link glyph
 * (IconLink) read as generic "connection" at 16-20px in the admin sidebar
 * hover — technically correct as the junction mark, but not
 * intuitively "assigned people." Redrawn to Lucide `user-check`
 * vocabulary: a person + checkmark reads immediately as "who is
 * assigned / who has a grant." IconLink stays as the junction /
 * generic-connection glyph elsewhere (junction ObjectIcon kind).
 */
export function IconUserCheck(props: IconProps = {}) {
  return svg(
    <>
      {/* Person head + shoulders — mirrors IconPeople's primary-person
         proportions so the two glyphs read as siblings in the same nav. */}
      <circle cx="6" cy="6" r="2.3" />
      <path d="M2 13c0-2.2 1.8-4 4-4s4 1.8 4 4" />
      {/* Push 53 (2026-07-15) — checkmark redrawn with more bounce so it
         reads clearly at 16-20px. Prior path had only 1.5-unit vertical
         travel on the mid-dip; new path gives 2 units up + 3 units down
         which mirrors Lucide user-check's proportions and makes the tick
         legible at Assignments-tab size. */}
      <path d="M10 7.5l1.8 1.8L14.6 5.5" />
    </>,
    props,
  );
}

/**
 * Phase 2 Block 2B icon mapping (2026-06-30) — per-table marks.
 * Five new glyphs covering the remaining inventory:
 *   - IconBell        sys__notification
 *   - IconFolder      chat__folder
 *   - IconMessage     chat__message
 *   - IconClipboard   billing__approval_request
 *   - IconPackage     billing__product
 * Designed to read at 12-30px (the ObjectIcon glyph size range);
 * stroke-only paths so they inherit the tile's white-on-color treatment.
 */

// Push 5-post-smoke-25a (2026-07-14): redrawn cleaner. Prior dome path
// used 3-anchor bezier that read chunky at small sizes (the sides
// bowed outward in a swollen way). New shape uses Lucide `bell`
// vocabulary — dome semicircle top + two straight sides down to a
// clean horizontal rim + a small clapper arc below. Reads as a
// proper handbell at 12-24px.
export function IconBell(props: IconProps = {}) {
  return svg(
    <>
      {/* Dome + sides — arc from left rim, up over the top, down to right rim */}
      <path d="M4 11V8a4 4 0 0 1 8 0v3" />
      {/* Rim */}
      <path d="M3 11h10" />
      {/* Clapper */}
      <path d="M7 12.75a1.25 1.25 0 0 0 2 0" />
    </>,
    props,
  );
}

export function IconFolder(props: IconProps = {}) {
  return svg(
    <>
      {/* Folder back with the angled tab on the upper-left edge */}
      <path d="M2 5 L2 12.5 A0.7 0.7 0 0 0 2.7 13.2 H13.3 A0.7 0.7 0 0 0 14 12.5 V6.5 A0.7 0.7 0 0 0 13.3 5.8 H7.5 L6 4 H2.7 A0.7 0.7 0 0 0 2 5 Z" />
    </>,
    props,
  );
}

// Push 4-1c-d (2026-07-01) — Library pillar mark.
// Push 5-post-smoke-23 (2026-07-14) — redrawn from filled-book-shelf to
// Lucide `library` shape: four vertical spines with the rightmost leaning.
// Reads as "knowledge repository" — a shelf of resources — rather than
// "three books" (which the previous filled shape read as, per Ean's
// "book, not library" feedback). Stroke-only geometry matches the rest
// of the Icon.tsx primitives.
export function IconLibrary(props: IconProps = {}) {
  return svg(
    <>
      {/* Three upright spines */}
      <path d="M3 2.5V13.5" />
      <path d="M6 2.5V13.5" />
      <path d="M9 2.5V13.5" />
      {/* Leaning fourth book — angled to give the shelf depth */}
      <path d="M11.75 3.25L13.5 13.25" />
    </>,
    props,
  );
}

export function IconMessage(props: IconProps = {}) {
  return svg(
    <>
      {/* Speech bubble — rounded rectangle with a tail in the lower-left */}
      <path d="M3 4 H13 A1 1 0 0 1 14 5 V10 A1 1 0 0 1 13 11 H6.2 L3.8 13 V11 H3 A1 1 0 0 1 2 10 V5 A1 1 0 0 1 3 4 Z" />
    </>,
    props,
  );
}

export function IconClipboard(props: IconProps = {}) {
  return svg(
    <>
      {/* Outer board */}
      <path d="M4 4 H12 V14 H4 Z" />
      {/* Clip on top */}
      <path d="M6.5 3 H9.5 V5 H6.5 Z" />
      {/* Two inner list lines (the "list" in clipboard-list) */}
      <path d="M6 8 H10.5" />
      <path d="M6 10.5 H10.5" />
    </>,
    props,
  );
}

// Push 64 Sub-agent YY (2026-07-16) — file-text glyph for the Terms of
// Service legal tab. Distinct from IconClipboard (which the parent
// Legal & policies page uses via 'approval-request'). A folded-corner
// document sheet with three horizontal text lines reads immediately as
// "written agreement" — the Lucide `file-text` vocabulary.
export function IconFileText(props: IconProps = {}) {
  return svg(
    <>
      {/* Page outline with folded top-right corner */}
      <path d="M4 2 H9.5 L13 5.5 V13 A1 1 0 0 1 12 14 H4 A1 1 0 0 1 3 13 V3 A1 1 0 0 1 4 2 Z" />
      {/* Corner fold — the triangular flap at top-right */}
      <path d="M9.5 2 V5.5 H13" />
      {/* Three text lines inside the body */}
      <path d="M5.5 8.5 H10.5" />
      <path d="M5.5 10.5 H10.5" />
      <path d="M5.5 12.5 H8" />
    </>,
    props,
  );
}

export function IconPackage(props: IconProps = {}) {
  return svg(
    <>
      {/* Box outline — square */}
      <path d="M2.5 5 L8 2.5 L13.5 5 V11 L8 13.5 L2.5 11 Z" />
      {/* Lid seam — horizontal split at the top half */}
      <path d="M2.5 5 L8 7.5 L13.5 5" />
      {/* Center vertical — gives the box its "3D box" shape */}
      <path d="M8 7.5 V13.5" />
    </>,
    props,
  );
}

// Push 4-1o (2026-07-09) — eye glyph for the Privacy legal tab. Distinct
// from IconLock (used by 'permission-sets') so users don't confuse "your
// privacy" with "workspace permission sets" per Ean's ask. Simple almond
// outline + iris + pupil, matches the Icon.tsx stroke aesthetic.
export function IconEye(props: IconProps = {}) {
  return svg(
    <>
      <path d="M1.5 8 C3.5 4.5 5.5 3 8 3 s4.5 1.5 6.5 5 C12.5 11.5 10.5 13 8 13 S3.5 11.5 1.5 8 Z" />
      <circle cx="8" cy="8" r="2.4" />
      <circle cx="8" cy="8" r="0.9" fill="currentColor" />
    </>,
    props,
  );
}

// Push 4-1o (2026-07-09) — cookie glyph for the Cookies legal tab. Round
// disk with three chocolate-chip dots. Distinct from IconCoin (which is a
// smooth disk used by 'token-debit'). Reads as "cookie" without being
// cartoonish, matches the stroke aesthetic.
export function IconCookie(props: IconProps = {}) {
  return svg(
    <>
      <path d="M8 1.5 a6.5 6.5 0 1 0 6.5 6.5 A3 3 0 0 1 11 5 A2.4 2.4 0 0 1 8 1.5 Z" />
      <circle cx="5.5" cy="8.5" r="0.7" fill="currentColor" />
      <circle cx="9" cy="10.5" r="0.7" fill="currentColor" />
      <circle cx="10.5" cy="7.5" r="0.55" fill="currentColor" />
    </>,
    props,
  );
}

// ---------------------------------------------------------------------
// Push 5-C (2026-07-11) — modern iconography refresh for the chat surface.
// Twelve new marks matching the Lucide stroke aesthetic already in use.
// Each icon inherits the SVG primitive's currentColor + strokeWidth so
// they slot into any surrounding treatment (hover row, tool card, cost
// chip). Sized to the 16-grid the rest of Icon.tsx uses.
// ---------------------------------------------------------------------

// IconChatPlus — "New chat" affordance across the chat history rail +
// rail collapsed pill.
// Push 5-post-smoke-23 (2026-07-14): shape redrawn from bubble+plus to
// Lucide `square-pen` (pencil-writes-on-page). Matches Anthropic /
// Claude.ai vocabulary — the "compose a new message" glyph everyone
// converges on. Left path is an open rectangle (top-right corner
// missing so the pencil can enter); right path is the pencil body,
// tip pointing into the square, eraser out top-right.
export function IconChatPlus(props: IconProps = {}) {
  return svg(
    <>
      {/* Open rectangle — top-right corner intentionally missing so
         the pencil path visually "writes into" the page. */}
      <path d="M8 2.5H3.5A1 1 0 0 0 2.5 3.5V12.5A1 1 0 0 0 3.5 13.5H12.5A1 1 0 0 0 13.5 12.5V8" />
      {/* Pencil body — diagonal parallelogram with a tip at bottom-left,
         eraser at top-right. Standard Lucide square-pen geometry
         scaled to the 16-grid. */}
      <path d="M11.5 1.75L14.25 4.5L8 10.75L5.25 11.25L5.75 8.5Z" />
    </>,
    props,
  );
}

// IconChatSparkle — chat bubble with a small sparkle. Signals
// AI-generated content — used above assistant response actions,
// in empty-state welcome copy, and on the "AI thinking" indicator.
// Push 5-post-smoke-25a (2026-07-14): interior sparkle rebalanced
// to match the new IconSparkle geometry — same pointed-ray ratio
// so both sparkle glyphs read as siblings across the surface.
export function IconChatSparkle(props: IconProps = {}) {
  return svg(
    <>
      <path d="M2.5 4a1.5 1.5 0 0 1 1.5-1.5h8A1.5 1.5 0 0 1 13.5 4v5A1.5 1.5 0 0 1 12 10.5H6L3.5 13V4z" />
      {/* Interior sparkle — pointed 4-arm ray matching IconSparkle */}
      <path d="M7.5 4.5L8 6.5L10 7L8 7.5L7.5 9.5L7 7.5L5 7L7 6.5Z" />
    </>,
    props,
  );
}

// IconToolCall — nested angle brackets. Marks any tool-call widget so
// the user visually parses "the assistant called a tool" without reading
// the whole chip. Sits inside the tool-call card header.
export function IconToolCall(props: IconProps = {}) {
  return svg(
    <>
      <path d="M5 4L2 8l3 4" />
      <path d="M11 4l3 4-3 4" />
      <path d="M9.5 4l-3 8" />
    </>,
    props,
  );
}

// IconThinking — three-dot ellipsis in a rounded rect. Marks the
// reasoning / extended-thinking collapsible panel that sits above the
// assistant response body. Reads as "the model paused to reason."
export function IconThinking(props: IconProps = {}) {
  return svg(
    <>
      <rect x="2" y="5" width="12" height="6" rx="2" />
      <circle cx="5.5" cy="8" r="0.8" fill="currentColor" />
      <circle cx="8" cy="8" r="0.8" fill="currentColor" />
      <circle cx="10.5" cy="8" r="0.8" fill="currentColor" />
    </>,
    props,
  );
}

// IconCitation — small numbered marker. Rendered inline inside AI
// responses to mark a reference to a Library document (Push 5-C Item 2).
// The tinted disk reads as "footnote" / "reference" without depending on
// the specific number rendered above it.
// Push 5-post-smoke-25a (2026-07-14): interior mark redrawn from "?"
// (which duplicated IconHelp's meaning) to a footnote "1" numeral so
// the two glyphs read distinctly. Semantics — IconCitation marks a
// specific footnote inside an AI response; IconHelp is generic "get
// help." Number 1 is a placeholder shape (users don't parse the exact
// digit at 12-16px); the "n-in-a-circle" silhouette is the universal
// footnote convention.
export function IconCitation(props: IconProps = {}) {
  return svg(
    <>
      <circle cx="8" cy="8" r="5.5" />
      {/* Numeral "1" — diagonal serif into vertical stem */}
      <path d="M6.75 6.25L8 5.5V10.75" />
      {/* Base serif */}
      <path d="M6.75 10.75H9.5" />
    </>,
    props,
  );
}

// IconRegenerate — circular arrow (~2/3 arc + arrow head). Standard SaaS
// vocabulary for "run this again." Replaces the ad-hoc circular arrow
// SVG that MessageItem.tsx renders inline for the assistant Regenerate
// action.
export function IconRegenerate(props: IconProps = {}) {
  return svg(
    <>
      <path d="M2.5 8a5.5 5.5 0 0 1 9.5-3.8" />
      <path d="M12 2v3H9" />
      <path d="M13.5 8a5.5 5.5 0 0 1-9.5 3.8" />
      <path d="M4 14v-3h3" />
    </>,
    props,
  );
}

// IconThumbsUp — legacy affordance. Push 61 (2026-07-16) retired the
// thumbs-up / thumbs-down feedback buttons from MessageActions per Ean's
// "we aren't a model" directive; the response_feedback event kind stays
// only for backward audit compatibility of existing rows. Icon primitive
// kept here for any future non-model surface that wants it.
export function IconThumbsUp(props: IconProps = {}) {
  return svg(
    <>
      <path d="M2.5 7.5h2.5v6H2.5z" />
      <path d="M5 7.5l2.2-4.5a1 1 0 0 1 1.8 0.4V7h3.5a1.4 1.4 0 0 1 1.4 1.6l-0.6 3.8A1.5 1.5 0 0 1 11.8 13.5H5" />
    </>,
    props,
  );
}

// IconThumbsDown — response feedback affordance (negative). Mirror of
// IconThumbsUp for symmetry in the hover row.
export function IconThumbsDown(props: IconProps = {}) {
  return svg(
    <>
      <path d="M13.5 8.5H11V2.5h2.5z" />
      <path d="M11 8.5l-2.2 4.5a1 1 0 0 1-1.8-0.4V9H3.5a1.4 1.4 0 0 1-1.4-1.6l0.6-3.8A1.5 1.5 0 0 1 4.2 2.5H11" />
    </>,
    props,
  );
}

// IconBranch — Y-shaped Git-style branch. Replaces the inline branch
// SVG in MessageItem.tsx so the "Branch from here" affordance in the
// hover row shares vocabulary with the fork endpoint copy.
export function IconBranch(props: IconProps = {}) {
  return svg(
    <>
      <circle cx="4" cy="3" r="1.5" />
      <circle cx="4" cy="13" r="1.5" />
      <circle cx="12" cy="7" r="1.5" />
      <path d="M4 4.5v7" />
      <path d="M4 8h4a2 2 0 0 0 2-2V5" />
    </>,
    props,
  );
}

// IconCopy — overlapping rounded squares. Replaces the inline copy SVG
// in MessageItem.tsx for the hover-row Copy button, and slots into any
// future "copy to clipboard" affordance across the app.
export function IconCopy(props: IconProps = {}) {
  return svg(
    <>
      <rect x="5" y="5" width="9" height="9" rx="1.5" />
      <path d="M3 11V3.5a1.5 1.5 0 0 1 1.5-1.5H11" />
    </>,
    props,
  );
}

// IconStop — filled small square. Marks the "Stop generation" affordance
// in the composer button cluster. Reads as "halt" against the outlined
// icons the rest of the composer uses.
export function IconStop(props: IconProps = {}) {
  return svg(
    <rect x="4" y="4" width="8" height="8" rx="1.5" fill="currentColor" stroke="none" />,
    props,
  );
}

// IconAttach — paperclip alias for callers that expect the "Attach"
// vocabulary. Delegates to IconPaperclip so the visual shape stays
// consistent with the pre-Push-5-C composer footer paperclip.
export function IconAttach(props: IconProps = {}) {
  return IconPaperclip(props);
}

// IconFromLibrary — book with a small inward arrow. Marks the composer
// "From Library" affordance that opens the LibraryPicker modal per
// Push 5-C Item 6 + Item 8. Distinct from IconLibrary (three-book shelf
// = pillar mark). This is the arrow-in variant, reading as "reference
// a doc from your Library."
export function IconFromLibrary(props: IconProps = {}) {
  return svg(
    <>
      {/* Book cover */}
      <path d="M3 3h7.5a1.5 1.5 0 0 1 1.5 1.5V12" />
      <path d="M12 13.5H4.5A1.5 1.5 0 0 1 3 12V3" />
      {/* Small down-right arrow inside — "pull in" affordance */}
      <path d="M6.5 6.5l2.5 2.5" />
      <path d="M9 8V6" />
      <path d="M7 8h2" />
    </>,
    props,
  );
}

// ---------------------------------------------------------------------
// Push 5-F (2026-07-11) — consolidation additions. Callsites currently
// hand-roll these shapes inline; adding them here lets follow-up work
// migrate to canonical vocabulary without any visual regression.
// Not migrating callsites in this push (see Push 5-F report for the
// "consolidation opportunities" list); primitives-only landing keeps the
// scope tight while unblocking the next sweep.
// ---------------------------------------------------------------------

// IconEyeOff — companion to IconEye for password-visibility toggles. Same
// almond outline as IconEye plus a strike-through slash, on the 16-grid
// shared with the rest of Icon.tsx.
export function IconEyeOff(props: IconProps = {}) {
  return svg(
    <>
      {/* Almond outline (matches IconEye) */}
      <path d="M1.5 8 C3.5 4.5 5.5 3 8 3 s4.5 1.5 6.5 5 C12.5 11.5 10.5 13 8 13 S3.5 11.5 1.5 8 Z" />
      {/* Iris */}
      <circle cx="8" cy="8" r="2.4" />
      {/* Diagonal strike-through */}
      <path d="M2 2 L14 14" />
    </>,
    props,
  );
}

// IconHelp — question-mark inside a circle. HelpIcon.tsx currently owns
// this shape locally; hoisting to Icon.tsx makes the mark reusable and
// removes duplication when other help-affordances land.
export function IconHelp(props: IconProps = {}) {
  return svg(
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M6 6.5 a2 2 0 0 1 4 0 c0 1 -1 1.5 -2 2 v0.5" />
      <circle cx="8" cy="11.2" r="0.45" fill="currentColor" />
    </>,
    props,
  );
}

// IconInfoCircle — solid dot + tall bar inside a circle. Modern SaaS
// info affordance (Notion / Linear / Stripe). Distinct from IconAlert
// (triangle = warning) and IconHelp (question-mark = help).
export function IconInfoCircle(props: IconProps = {}) {
  return svg(
    <>
      <circle cx="8" cy="8" r="6" />
      <circle cx="8" cy="5.2" r="0.55" fill="currentColor" />
      <path d="M8 7.5 V11.5" />
    </>,
    props,
  );
}

// ---------------------------------------------------------------------
// Push 5-post-smoke-1 (2026-07-11) — chat rail iOS polish. Two new
// mark categories:
//  - IconFolderPlus — the "New folder" affordance in the rail header
//    (replaces the inline SVG previously hand-rolled in RailHeader).
//  - Menu-item icons for the chat row overflow: IconPencil (Rename),
//    IconArchive, IconDownload, IconMailUnread + IconMailRead
//    (Mark as unread / Mark as read), IconPinOff, IconMove.
// Each icon inherits the SVG primitive's currentColor + strokeWidth so
// they slot into the row menu at 14px.
// ---------------------------------------------------------------------

// IconFolderPlus — folder outline with a small "+" glyph nested in the
// bottom-right corner. Reads as "create a new folder" at a glance without
// borrowing from the generic IconPlus (used for prompt insertion). Sized
// to the 16-grid so it aligns with the IconChatPlus + IconChevron cluster
// in the rail header.
// Push 5-post-smoke-23 (2026-07-14): shape redrawn to Lucide
// `folder-plus`. Prior version nested the plus glyph OUTSIDE the folder
// body at the bottom-right, which read as "second folder" rather than
// "add to folder." Lucide places the plus INSIDE the folder body — the
// "add" affordance sits where new content would land. Cleaner mental
// model, aligned with Claude.ai / Notion / Linear vocabulary.
// Push 5-post-smoke-47 Sub-agent II (2026-07-15): folder outline bumped
// ~10% larger (left edge tightened from x=2 → x=1.5; right edge from
// x=14 → x=14.5; bottom from y=13.2 → y=13.7; tab notch nudged up) so
// the inner "+" glyph has more breathing room. Plus glyph grown from
// 3.5x3.5 units to 5x5 units.
// Push 49 Sub-agent KK (2026-07-15): plus glyph SHRUNK from 5x5 back
// to 4x4 units and shifted down slightly so the arms sit clearly inside
// the folder body — Sub-agent II's 5x5 growth was too aggressive; at
// 20px render size (RailHeader) the plus visually touched / crossed
// the tab notch and top-right body edge. New geometry: vertical arm
// y=7.5→11.5 (was 7→12), horizontal arm x=6→10 at y=9.5 (was 5.5→10.5
// at y=9.5). Folder outline unchanged — the ~82% width still reads as
// "big enough" per Sub-agent II's rationale; only the interior glyph
// shrinks so there's visible whitespace between plus arms and outline.
// Ean directive: "Make the + fit inside the folder, shouldn't be are
// to not have the lines collide."
export function IconFolderPlus(props: IconProps = {}) {
  return svg(
    <>
      {/* Full folder body — outline at ~82% of the 16-viewBox width. */}
      <path d="M1.5 4.5 L1.5 13 A0.7 0.7 0 0 0 2.2 13.7 H13.8 A0.7 0.7 0 0 0 14.5 13 V6.2 A0.7 0.7 0 0 0 13.8 5.5 H7.5 L5.8 3.75 H2.2 A0.7 0.7 0 0 0 1.5 4.5 Z" />
      {/* Plus glyph — 4x4 units centered at (8, 9.5). Clearance above
         (y=7.5 vs body top y=5.5 = 2 units) and below (y=11.5 vs body
         bottom y=13.7 = 2.2 units) so the arms sit clearly inside the
         body outline at every render size. */}
      <path d="M8 7.5 V11.5" />
      <path d="M6 9.5 H10" />
    </>,
    props,
  );
}

// IconPencil — rename affordance. Diagonal pencil body with eraser + tip.
// Standard Lucide-shape edit glyph; used in the chat row overflow menu
// alongside IconArchive / IconTrash / etc.
export function IconPencil(props: IconProps = {}) {
  return svg(
    <>
      <path d="M11.5 2.5 L13.5 4.5 L5 13 L2.5 13.5 L3 11 Z" />
      <path d="M10 4 L12 6" />
    </>,
    props,
  );
}

// IconArchive — outbound "put this in the archive box" affordance. Lid
// on top + open box below + inward arrow suggesting the "move into"
// action. Distinct from IconInbox (which points down into a container
// with side flaps).
export function IconArchive(props: IconProps = {}) {
  return svg(
    <>
      {/* Lid */}
      <path d="M2 3.5 H14 V6 H2 Z" />
      {/* Body */}
      <path d="M3 6 V13 A0.5 0.5 0 0 0 3.5 13.5 H12.5 A0.5 0.5 0 0 0 13 13 V6" />
      {/* Grip / handle slot on lid — small notch */}
      <path d="M6.5 9 H9.5" />
    </>,
    props,
  );
}

// IconDownload — arrow-into-tray glyph. Vertical arrow pointing down at a
// short horizontal tray line. Standard SaaS "download to disk" vocabulary.
export function IconDownload(props: IconProps = {}) {
  return svg(
    <>
      <path d="M8 2.5 V10" />
      <path d="M4.5 7 L8 10.5 L11.5 7" />
      <path d="M3 13 H13" />
    </>,
    props,
  );
}

// Push 48 (2026-07-15) — IconMailUnread + IconMailRead removed. The
// read/unread flip in the chat row menu migrated to IconEye / IconEyeOff
// in Push 5-post-smoke-47 (Sub-agent II); the envelope+badge variants
// carried no remaining callsites. See SessionItem.tsx for the eye-based
// vocabulary + Ean's directive that motivated the swap.

// IconPinOff — pushpin with a diagonal strike-through. Signals the
// "Unpin" flip in the chat row menu when the session is currently
// pinned. Same diagonal thumbtack body as IconPin (Push 64 redraw)
// plus a slash across the icon.
export function IconPinOff(props: IconProps = {}) {
  // Push 64 Sub-agent YY (2026-07-16) — paired with the diagonal IconPin.
  // Push 68 Sub-agent CCC (2026-07-17) — rotation flipped -45° → +45° to
  // track IconPin (see IconPin doc for the "match the pencil slant"
  // rationale). The strike-through path also flips so it stays
  // perpendicular to the new pin shaft direction: pin shaft now runs
  // upper-right → lower-left, so the strike runs upper-left → lower-right
  // (M2 2 → L14 14) to cross it at ~90°.
  return svg(
    <>
      <g transform="rotate(45 8 8)">
        <path d="M6 2 H10 V5 H12 V8 H9 V12 L8 14 L7 12 V8 H4 V5 H6 Z" />
      </g>
      {/* Diagonal strike-through — perpendicular to the pin's shaft */}
      <path d="M2 2 L14 14" />
    </>,
    props,
  );
}

// IconMove — closed folder outline + horizontal (sideways) arrow inside
// the body pointing right. Push 49 Sub-agent KK (2026-07-15) redraw per
// Ean directive: "Have the move folder be a folder with a sideways arrow
// inside it again NOT Having Lines collide."
//
// History:
//  - Push 37 Sub-agent Z redrew this to Lucide `folder-input` (folder
//    with top-left open + downward arrow descending into the tab).
//  - Push 47 Sub-agent II bumped the folder outline ~10% larger; the
//    downward-arrow shaft then visually crossed the folder tab edge at
//    render size.
//  - Push 49 (this pass) reverts to the pre-Push-37 semantic (a closed
//    folder with a horizontal arrow *inside* the body pointing right).
//    Fixes the collision by (a) closing the folder outline again (no
//    top-left cutout — the arrow lives fully inside the body, so the
//    open corner isn't needed) and (b) drawing the arrow well inside
//    the interior with clearance from all four walls.
//
// Geometry: folder outline matches IconFolderPlus's ~82% width shape
// (closed body with tab on top-left). Horizontal arrow shaft at y=9.5
// (body vertical center is y≈9.6), spanning x=4.5 to x=11 — arrowhead
// tip at (11, 9.5) sits ~3.5 units clear of the right body edge (x=14.5)
// and shaft start at x=4.5 sits ~3 units clear of the left body edge
// (x=1.5). Vertical clearance: arrowhead spans y=8 to y=11, so ~2.5
// units clear of body top (y=5.5) and ~2.7 units clear of body bottom
// (y=13.7). No stroke overlaps at any render size.
export function IconMove(props: IconProps = {}) {
  return svg(
    <>
      {/* Closed folder outline — same shape as IconFolderPlus so both
         "folder + inner glyph" icons share silhouette. Tab on top-left,
         body ~82% of viewBox width. */}
      <path d="M1.5 4.5 L1.5 13 A0.7 0.7 0 0 0 2.2 13.7 H13.8 A0.7 0.7 0 0 0 14.5 13 V6.2 A0.7 0.7 0 0 0 13.8 5.5 H7.5 L5.8 3.75 H2.2 A0.7 0.7 0 0 0 1.5 4.5 Z" />
      {/* Horizontal arrow inside body — shaft y=9.5, from x=4.5 to x=11;
         arrowhead pointing right at (11, 9.5). Clear whitespace on all
         four sides between arrow and folder outline. */}
      <path d="M4.5 9.5 H11" />
      <path d="M9.5 8 L11 9.5 L9.5 11" />
    </>,
    props,
  );
}

// ---------------------------------------------------------------------
// Push 5-post-smoke-2 (2026-07-11) — chat rail filter panel additions.
// Three new marks for the Slack-inspired restructure + the filter
// popover triggered from the rail header. IconSliders is the canonical
// "filter / adjust" affordance (two horizontal bars with dot markers,
// mirroring Claude.ai's filter icon); IconStar + IconClock are the
// leading-icon glyphs on the Starred / Recent section headers.
// ---------------------------------------------------------------------

// IconFunnel — Push 5-post-smoke-28 Sub-agent P (2026-07-15). Canonical
// "filter" glyph (Lucide `filter` / Heroicons funnel). Three descending
// horizontal lines that taper into a point — reads as "narrow the set"
// at a glance. Ean directive: "Can you make the filter chats icon an
// actual filter." Distinct from IconSliders (two adjustment tracks with
// dot handles — used for "adjust / tune settings" surfaces).
export function IconFunnel(props: IconProps = {}) {
  return svg(
    <>
      {/* Funnel outline — wide open at the top, narrowing to a spout */}
      <path d="M2 3H14L9.5 9V13.5L6.5 12V9L2 3Z" />
    </>,
    props,
  );
}

// IconSliders — two horizontal sliders with round dot handles. Modern
// SaaS vocabulary for "adjust / tune / configure this list." Distinct
// from IconFunnel (canonical filter/funnel glyph) — sliders reads as
// "tune the knobs" where funnel reads as "narrow the set." Used on
// surfaces that let the user adjust values without changing the axis.
export function IconSliders(props: IconProps = {}) {
  return svg(
    <>
      {/* Top slider — track + dot handle offset right */}
      <path d="M2.5 5 H13.5" />
      <circle cx="10" cy="5" r="1.6" fill="currentColor" stroke="none" />
      {/* Bottom slider — track + dot handle offset left */}
      <path d="M2.5 11 H13.5" />
      <circle cx="6" cy="11" r="1.6" fill="currentColor" stroke="none" />
    </>,
    props,
  );
}

// IconStar — five-point star outline. Slack-inspired section-header
// leading icon for the Starred (pinned chats) section. Distinct from
// IconPin (pushpin, used inline on row titles to mark individual
// pinned rows). Star = "this section groups the pinned ones";
// pushpin = "this specific row is pinned."
export function IconStar(props: IconProps = {}) {
  return svg(
    <path d="M8 2 L9.7 6.2 L14 6.7 L10.8 9.6 L11.7 14 L8 11.7 L4.3 14 L5.2 9.6 L2 6.7 L6.3 6.2 Z" />,
    props,
  );
}

// IconClock — circular clock with hour + minute hands pointing to 10:30.
// Section-header leading icon for the Recent (unfoldered chats) section
// — pairs with IconStar (Starred) + IconFolder (Folders) as the trio of
// section markers in the Slack-inspired restructure.
export function IconClock(props: IconProps = {}) {
  return svg(
    <>
      <circle cx="8" cy="8" r="6" />
      {/* Hour hand — up */}
      <path d="M8 4.5 V8" />
      {/* Minute hand — right */}
      <path d="M8 8 L10.5 8" />
    </>,
    props,
  );
}

// ---------------------------------------------------------------------
// Push 5-post-smoke-22 (2026-07-14) — chat-surface iconography overhaul.
// Three consolidation glyphs to close out the vocabulary gap Ean flagged
// on the chat kebab menus, composer web-search chip, and Duplicate/Fork
// affordances. Each of these was being hand-rolled inline across surface
// files (⋯ text glyph on 4 different files, custom globe SVG in Composer);
// hoisting to Icon.tsx gives the whole chat surface one primitive per
// mark and lets the follow-up sweep collapse callsite drift.
// ---------------------------------------------------------------------

// IconMoreHorizontal — three horizontal dots, canonical Lucide-family
// "more actions" / kebab affordance. Replaces the text `⋯` glyph across
// SessionItem (chat row kebab), FolderTree (folder row kebab), and
// InlineChatTitle (session-header kebab). Small filled dots so it reads
// at 14-16px sizes without the stroke lines collapsing. Sibling to the
// DotsIcon in KebabMenu.tsx (which stays local to that component for the
// Stripe-style row-action pattern); this Icon.tsx primitive is the
// chat-surface canonical.
export function IconMoreHorizontal(props: IconProps = {}) {
  return svg(
    <>
      <circle cx="3.5" cy="8" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="8" cy="8" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="12.5" cy="8" r="1.2" fill="currentColor" stroke="none" />
    </>,
    props,
  );
}

// IconGlobe — sphere with latitude/longitude lines. Replaces the inline
// SVG previously hand-rolled in Composer.tsx for the web-search chip.
// Lucide-style two-oval-plus-horizon shape reads as "web / browse" at
// small sizes. Sits next to the "Web · on" / "Web · off" label in the
// composer chip; also available for any future "search the web" affordances.
export function IconGlobe(props: IconProps = {}) {
  return svg(
    <>
      <circle cx="8" cy="8" r="6" />
      {/* Horizon */}
      <path d="M2 8h12" />
      {/* Meridian — narrow oval reads as the vertical "slice" */}
      <path d="M8 2c2 2 2 10 0 12M8 2c-2 2-2 10 0 12" />
    </>,
    props,
  );
}

// ---------------------------------------------------------------------
// Push 5-post-smoke-23 (2026-07-14) — modern-SaaS chat surface glyphs.
// Two new primitives to close out Ean's visual-overhaul round 2 feedback:
//  - IconArrowUp: composer Send button. Filled brand-teal tile + white
//    arrow inside. Anthropic/Claude/iMessage convergence — a filled
//    up-arrow tile as the "send this" affordance beats a text button.
//  - IconVolume: assistant message hover row "Read aloud" affordance.
//    Speaker cone + two arc-line sound waves. Standard TTS vocabulary.
// ---------------------------------------------------------------------

// IconArrowUp — clean up-arrow with round line caps + joins. Fills the
// composer Send button (filled teal tile, white icon inside). Also
// available for any future "send / submit / promote" affordances.
export function IconArrowUp(props: IconProps = {}) {
  return svg(
    <>
      <path d="M8 13.5V3" />
      <path d="M3.5 7.5L8 3L12.5 7.5" />
    </>,
    props,
  );
}

// IconVolume — speaker + two waves. Reads as "read aloud / play audio."
// Used in the assistant message hover row as the TTS affordance. Body
// is a compact speaker cone; two right-of-cone arcs suggest sound.
export function IconVolume(props: IconProps = {}) {
  return svg(
    <>
      {/* Speaker cone */}
      <path d="M2.5 6.5H5L8.5 3.5V12.5L5 9.5H2.5Z" />
      {/* Two sound waves — inner short + outer long */}
      <path d="M10.5 6.5C11.5 7.25 11.5 8.75 10.5 9.5" />
      <path d="M12.5 4.75C14 6 14 10 12.5 11.25" />
    </>,
    props,
  );
}

// IconDuplicate — two offset squares. Standard SaaS "duplicate this
// record" vocabulary (Notion, Linear, Figma converge on this shape).
// Used on the InlineChatTitle overflow "Duplicate chat" menu item so
// the affordance reads at a glance next to Rename/Share/Delete. Similar
// silhouette to IconCopy (both are offset-squares) but the outer square
// is not clipped — reads as "duplicate the whole thing" vs Copy's
// "copy this content to clipboard."
export function IconDuplicate(props: IconProps = {}) {
  return svg(
    <>
      <rect x="5" y="5" width="9" height="9" rx="1.5" />
      <path d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2H3.5A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5" />
    </>,
    props,
  );
}

// ---------------------------------------------------------------------
// Push 5-post-smoke-33 (Sub-agent V, 2026-07-15) — Analytics-lane
// vocabulary. Three glyphs to close out the parent + child icon walkback
// (Ean directive: sub-child icons should be UNIQUE and LOGICAL, not the
// same-icon-repeated pattern that Push 32 killed and prior IA carried):
//   - IconBarChart   → Analytics parent tile (was 'activity'/IconSparkle
//                       — sparkle now reserved for Insights child)
//   - IconDashboard  → Overview child (aggregate view of the analytics
//                       bucket — distinct from IconLayers/Capabilities
//                       parent + IconGrid/Market child)
//   - IconPulse      → Activity child (heartbeat/activity-monitor line
//                       reads as "raw event feed" without duplicating
//                       IconWave which is the Prompts pillar glyph)
// ---------------------------------------------------------------------

// IconBarChart — three ascending bars sitting on a baseline. Canonical
// "analytics / metrics" vocabulary (Lucide `bar-chart-3`, Heroicons
// chart-bar). Reads as "numbers and trends" at every nav-tile size.
export function IconBarChart(props: IconProps = {}) {
  return svg(
    <>
      {/* Baseline */}
      <path d="M2.5 13.5H13.5" />
      {/* Three bars — ascending heights, evenly spaced */}
      <path d="M4 13.5V9.5" />
      <path d="M8 13.5V6" />
      <path d="M12 13.5V3.5" />
    </>,
    props,
  );
}

// IconDashboard — four-panel rectangle grid. Reads as "dashboard /
// overview panel layout" — the aggregate view of many widgets. Distinct
// silhouette from IconLayers (stacked pancakes = capability types) and
// IconGrid (4 discrete rounded tiles = market catalog). This one is a
// unified outer border split into 4 quadrants — one dashboard, four
// zones, which is the correct mental model for an Overview page.
export function IconDashboard(props: IconProps = {}) {
  return svg(
    <>
      <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" />
      {/* Vertical divider — offset slightly right so left panel is wider */}
      <path d="M6.75 3V13" />
      {/* Horizontal divider on the right side only — top-right + bottom-right */}
      <path d="M6.75 8H13.5" />
    </>,
    props,
  );
}

// IconPulse — heartbeat / activity-monitor line with one clear spike.
// Canonical vocabulary for "live activity / events happening" (Lucide
// `activity`, Apple Health heart rate). Distinct from IconWave (two
// sine waves = Prompts pillar) and IconChart (bar chart = analytics
// parent). Two flat baseline segments with a triangular spike in the
// middle — reads immediately as "activity feed / events over time"
// at nav-tile scale.
export function IconPulse(props: IconProps = {}) {
  return svg(<path d="M2 8H4.5L6 4.5L9 12L10.5 8H14" />, props);
}

/**
 * IconFlag — Push 61 (Sub-agent WW, 2026-07-16). Lucide-style rectangular
 * banner-flag on a vertical pole. Slotted into the assistant message-row
 * overflow menu as the "Report a problem" entry — replaces the thumbs
 * up/down affordances that shipped in Push 5-C. Rectangular banner (top
 * half of the pole) + a straight vertical shaft down to the ground line
 * anchor. Reads as "flag/report" at every size 12-24px.
 */
export function IconFlag(props: IconProps = {}) {
  return svg(
    <>
      <path d="M3 2v12" />
      <path d="M3 2h9l-2 3 2 3H3" />
    </>,
    props,
  );
}
